import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { useForm } from '../../hooks';
import { Button, ButtonVariant, Input, InputVariant, Popup, Form } from '../Utils';

import * as gs from '../../global.module.scss';

const DueDateEditStep = React.memo(({ defaultValue, onUpdate, onBack, onClose }) => {
  const [t] = useTranslation();
  const [isDateError, setIsDateError] = useState(false);
  const [isTimeError, setIsTimeError] = useState(false);

  const [data, handleFieldChange, setData] = useForm(() => {
    const date = defaultValue || new Date().setHours(12, 0, 0, 0);

    return {
      date: t('format:date', {
        postProcess: 'formatDate',
        value: date,
      }),
      time: t('format:time', {
        postProcess: 'formatDate',
        value: date,
      }),
    };
  });

  const dateField = useRef(null);
  const timeField = useRef(null);

  const nullableDate = useMemo(() => {
    const date = t('format:date', {
      postProcess: 'parseDate',
      value: data.date,
    });
    return Number.isNaN(date.getTime()) ? null : date;
  }, [data.date, t]);

  const nullableDateTime = useMemo(() => {
    if (!nullableDate) {
      return null;
    }
    const value = t('format:dateTime', {
      postProcess: 'parseDate',
      value: `${data.date} ${data.time}`,
    });
    return Number.isNaN(value.getTime()) ? null : value;
  }, [data.date, data.time, nullableDate, t]);

  const handleDatePickerChange = useCallback(
    (date) => {
      setIsDateError(false);
      setData((prevData) => ({
        ...prevData,
        date: t('format:date', {
          postProcess: 'formatDate',
          value: date,
        }),
      }));
    },
    [setData, t],
  );

  const handleSubmit = useCallback(() => {
    if (!nullableDate) {
      dateField.current?.focus();
      setIsDateError(true);
      return;
    }
    if (!nullableDateTime) {
      timeField.current?.focus();
      setIsTimeError(true);
      return;
    }

    if (!defaultValue || nullableDateTime.getTime() !== defaultValue.getTime()) {
      onUpdate(nullableDateTime);
    }

    onClose();
  }, [nullableDate, nullableDateTime, defaultValue, onClose, onUpdate]);

  const handleClearClick = useCallback(() => {
    if (defaultValue) {
      onUpdate(null);
    }

    onClose();
  }, [defaultValue, onUpdate, onClose]);

  const handleKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case 'Enter': {
          handleSubmit();
          break;
        }
        default:
      }
    },
    [handleSubmit],
  );

  useEffect(() => {
    dateField.current?.focus({ preventScroll: true });
  }, []);

  return (
    <>
      <Popup.Header onBack={onBack}>{t('common.dueDate', { context: 'title' })}</Popup.Header>
      <Popup.Content isMinContent>
        <Form onKeyDown={handleKeyDown}>
          <Input
            ref={dateField}
            variant={InputVariant.Default}
            name="date"
            value={data.date}
            placeholder={t('common.enterDueDate')}
            onChange={handleFieldChange}
            isError={isDateError}
            onKeyDown={() => setIsDateError(false)}
          />
          <Input
            ref={timeField}
            variant={InputVariant.Default}
            name="time"
            value={data.time}
            placeholder={t('common.enterDueTime')}
            onChange={handleFieldChange}
            isError={isTimeError}
            onKeyDown={() => setIsTimeError(false)}
          />
          <DatePicker inline disabledKeyboardNavigation selected={nullableDate} onChange={handleDatePickerChange} />
          <div className={gs.controlsSpaceBetween}>
            <Button variant={ButtonVariant.Cancel} content={t('common.remove')} onClick={handleClearClick} />
            <Button variant={ButtonVariant.Submit} content={t('common.save')} onClick={handleSubmit} />
          </div>
        </Form>
      </Popup.Content>
    </>
  );
});

DueDateEditStep.propTypes = {
  defaultValue: PropTypes.instanceOf(Date),
  onUpdate: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

DueDateEditStep.defaultProps = {
  defaultValue: undefined,
  onBack: undefined,
};

export default DueDateEditStep;
